import { css } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { StatItem } from "@/uiKit/atoms/StatItem.tsx";

import { compareValues } from "./Common";
import { GlycemiaStatsBGMNoData } from "./GlycemiaStatsBGMNoData";

type GlycemiaStatsProps = {
  patientId: string;
  from: DateTime;
  to: DateTime;
  className?: string;
};

export const GlycemiaStatsBGM = ({ patientId, from, to, className }: GlycemiaStatsProps) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { data: glycemiaStats } = Queries.diabetes.useGlycemiaIndicators({
    patientId,
    from: from,
    to: to,
    glycemiaMeasurementMethod: "bgm",
  });

  const sizeOfRange = to.diff(from, "days").days;
  const { data: previousGlycemiaStats } = Queries.diabetes.useGlycemiaIndicators({
    patientId,
    from: from.minus({ days: sizeOfRange + 1 }),
    to: from.minus({ days: 1 }),
    glycemiaMeasurementMethod: "bgm",
  });

  if (!glycemiaStats) {
    return <GlycemiaStatsBGMNoData className={className} />;
  }

  return (
    <Card className={className}>
      <CardHeader title={t("glycemia.title")} />
      <CardContent className={styles.flexRow}>
        <StatItem
          title={t("pages.patientMonitoring.glycemia.averageMeasuresPerDay")}
          value={glycemiaStats.averageDailyMeasures as number}
          precision={1}
          variation={compareValues(glycemiaStats.averageDailyMeasures, previousGlycemiaStats?.averageDailyMeasures)}
          className={styles.child}
        />
        <StatItem
          title={t("pages.patientMonitoring.glycemia.averageReader")}
          value={glycemiaStats.meanGlycemia}
          unit={"mg/dL"}
          precision={0}
          variation={compareValues(glycemiaStats.meanGlycemia, previousGlycemiaStats?.meanGlycemia)}
          lowerIsBetter
          className={styles.child}
        />
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  flexRow: css`
    display: flex;
    gap: ${theme.spacing(8)};
    width: 100%;
  `,
  child: css`
    width: 50%;
  `,
});
